import { get, create } from "./apiService";
import { getAllAssets as getTradingAssetsMetadata } from "./assetService";
import {
  resolveBusinessRoute,
  resolveBusinessUrlWithQuery,
} from "../utils/businessRoute";
import { encodeApiPathSegment } from "../utils/apiPath";
import type {
  ApiResponse,
  LegacyAsset,
  V2Wallet,
  V2Balance,
  V2Portfolio,
  V2PortfolioAsset,
  PortfolioPerformance,
} from "../types/api";

const pendingRequests = new Map<string, Promise<unknown>>();

interface EnvelopeLike<T> {
  data?: T | EnvelopeLike<T>;
  status?: boolean;
  success?: boolean;
}

interface PortfolioGraphPoint {
  x: number;
  y: number;
}

interface PortfolioHistoryEntry {
  date?: string;
  value?: string | number;
}

interface AssetWalletRequest {
  assetId?: string;
  chainId?:
    | string
    | {
        _id?: string;
        symbol?: string;
        name?: string;
      };
  blockchainId?:
    | string
    | {
        _id?: string;
        symbol?: string;
        name?: string;
      };
  accountCategory?: "personal" | "business";
  businessId?: string | null;
}

interface CryptoReceiveAddressPayload {
  address?: string;
  memo?: string;
  tag?: string | null;
  isTagRequired?: boolean;
  asset?: string;
}

export interface WalletScopeParams {
  accountId?: string;
  businessId?: string;
}

export interface PortfolioEstimateRequest extends WalletScopeParams {
  currency?: string;
}

export interface PortfolioPerformanceRequest extends WalletScopeParams {
  startDate: string;
  endDate: string;
  interval?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object";
}

function extractResponseData<T>(body: unknown): T | undefined {
  if (!isRecord(body)) {
    return undefined;
  }

  const directData = (body as EnvelopeLike<T>).data;

  if (Array.isArray(directData)) {
    return directData as T;
  }

  if (
    isRecord(directData) &&
    ("data" in directData || "status" in directData || "success" in directData)
  ) {
    return extractResponseData<T>(directData);
  }

  if (directData !== undefined) {
    return directData as T;
  }

  return undefined;
}

function parseNumericValue(value: unknown): number {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
}

function normalizePortfolioPerformance(
  payload: PortfolioPerformance | Record<string, unknown> | null | undefined
): PortfolioPerformance {
  const record = isRecord(payload) ? payload : {};
  const history = Array.isArray(record.history)
    ? (record.history as PortfolioHistoryEntry[])
    : [];
  const graphDataFromPayload = Array.isArray(record.graphData)
    ? (record.graphData as PortfolioGraphPoint[])
        .filter(
          (point) =>
            typeof point?.x === "number" &&
            Number.isFinite(point.x) &&
            typeof point?.y === "number" &&
            Number.isFinite(point.y)
        )
        .map((point) => ({ x: point.x, y: point.y }))
    : [];

  const normalizedHistory = history
    .filter((entry) => typeof entry?.date === "string")
    .map((entry) => ({
      date: entry.date as string,
      value: String(entry.value ?? "0"),
    }));

  const graphData =
    graphDataFromPayload.length > 0
      ? graphDataFromPayload
      : normalizedHistory
          .map((entry) => {
            const timestamp = new Date(entry.date).getTime();
            if (!Number.isFinite(timestamp)) {
              return null;
            }

            return {
              x: timestamp,
              y: parseNumericValue(entry.value),
            };
          })
          .filter((entry): entry is PortfolioGraphPoint => entry !== null);

  const currentValue =
    parseNumericValue(record.currentValue) ||
    parseNumericValue(record.endValue) ||
    (graphData.length > 0 ? graphData[graphData.length - 1].y : 0);
  const startValue =
    parseNumericValue(record.startValue) ||
    (graphData.length > 0 ? graphData[0].y : 0);
  const allTimePerformance =
    parseNumericValue(record.allTimePerformance) ||
    parseNumericValue(record.changePercent);
  const profitOrLoss =
    typeof record.profitOrLoss === "string"
      ? record.profitOrLoss
      : typeof record.change === "string"
        ? record.change
        : `${currentValue - startValue}`;

  return {
    startDate: typeof record.startDate === "string" ? record.startDate : "",
    endDate: typeof record.endDate === "string" ? record.endDate : "",
    startValue: String(record.startValue ?? startValue),
    endValue: String(record.endValue ?? currentValue),
    change: typeof record.change === "string" ? record.change : profitOrLoss,
    changePercent:
      typeof record.changePercent === "string"
        ? record.changePercent
        : String(allTimePerformance),
    history: normalizedHistory,
    graphData,
    currentValue,
    allTimePerformance,
    profitOrLoss,
  };
}

async function dedup<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const existing = pendingRequests.get(key);
  if (existing) return existing as Promise<T>;

  const promise = fn().finally(() => {
    if (pendingRequests.get(key) === promise) {
      pendingRequests.delete(key);
    }
  });

  pendingRequests.set(key, promise);
  return promise;
}

const TTL_DEDUPED_RESPONSES = new Map<
  string,
  { promise: Promise<unknown>; expiresAt: number }
>();

async function dedupWithTtl<T>(
  key: string,
  ttlMs: number,
  fn: () => Promise<T>
): Promise<T> {
  const now = Date.now();
  const cached = TTL_DEDUPED_RESPONSES.get(key);
  if (cached && cached.expiresAt > now) {
    return cached.promise as Promise<T>;
  }

  const inFlight = pendingRequests.get(key);
  if (inFlight) {
    return inFlight as Promise<T>;
  }

  const promise = fn().finally(() => {
    if (pendingRequests.get(key) === promise) {
      pendingRequests.delete(key);
    }
  });

  pendingRequests.set(key, promise);
  TTL_DEDUPED_RESPONSES.set(key, { promise, expiresAt: now + ttlMs });

  setTimeout(() => {
    const current = TTL_DEDUPED_RESPONSES.get(key);
    if (current && current.promise === promise) {
      TTL_DEDUPED_RESPONSES.delete(key);
    }
  }, ttlMs);

  promise.catch(() => {
    const current = TTL_DEDUPED_RESPONSES.get(key);
    if (current && current.promise === promise) {
      TTL_DEDUPED_RESPONSES.delete(key);
    }
  });

  return promise;
}

const PORTFOLIO_ESTIMATE_TTL_MS = 2000;

function clearCacheEntriesByPrefix(prefix: string): void {
  for (const key of Array.from(pendingRequests.keys())) {
    if (key === prefix || key.startsWith(`${prefix}:`)) {
      pendingRequests.delete(key);
    }
  }
  for (const key of Array.from(TTL_DEDUPED_RESPONSES.keys())) {
    if (key === prefix || key.startsWith(`${prefix}:`)) {
      TTL_DEDUPED_RESPONSES.delete(key);
    }
  }
}

export const __resetWalletServiceCachesForTests = (): void => {
  pendingRequests.clear();
  TTL_DEDUPED_RESPONSES.clear();
};

function buildWalletScopeParams(
  scope?: WalletScopeParams
): Record<string, string> {
  const params: Record<string, string> = {};
  const businessId = scope?.businessId?.trim();

  if (businessId) {
    params.businessId = businessId;
    return params;
  }

  if (scope?.accountId) {
    params.accountId = scope.accountId;
  }

  return params;
}

function walletPath(personalPath: string, scope?: WalletScopeParams): string {
  return resolveBusinessRoute(scope?.businessId, personalPath);
}

function buildWalletScopeKey(
  prefix: string,
  scope?: WalletScopeParams & { currency?: string }
): string {
  const businessId = scope?.businessId?.trim() || "";
  const accountId = businessId ? "" : scope?.accountId?.trim() || "";
  const currency = scope?.currency?.trim().toUpperCase() || "default";
  return `${prefix}:${businessId || "personal"}:${accountId || "default"}:${currency}`;
}

function normalizeBlockchainIdentifier(
  value:
    | string
    | {
        _id?: string;
        symbol?: string;
        name?: string;
      }
    | undefined
): string | undefined {
  if (typeof value === "string" && value.trim()) {
    return value;
  }

  if (value && typeof value === "object" && typeof value._id === "string") {
    return value._id;
  }

  return undefined;
}

export const listWallets = async (
  scope?: WalletScopeParams
): Promise<{
  data: ApiResponse<V2Wallet[]>;
}> => {
  const data = await get<V2Wallet[]>(
    walletPath("/api/v2/wallets", scope),
    buildWalletScopeParams(scope)
  );
  return { data: { status: true, data } };
};

export const initDigitalWallet = async (
  scope?: WalletScopeParams
): Promise<{
  data: ApiResponse<V2Wallet[]>;
}> => {
  return listWallets(scope);
};

export const getWalletById = async (
  walletId: string
): Promise<{ data: ApiResponse<V2Wallet> }> => {
  const data = await get<V2Wallet>(
    `/api/v2/wallets/${encodeApiPathSegment(walletId, "walletId")}`
  );
  return { data: { status: true, data } };
};

export const getBalances = async (
  scope?: WalletScopeParams
): Promise<{
  data: ApiResponse<V2Balance[]>;
}> => {
  return dedup(buildWalletScopeKey("wallets_balances", scope), async () => {
    const data = await get<V2Balance[]>(
      walletPath("/api/v2/wallets/balances", scope),
      buildWalletScopeParams(scope)
    );
    return { data: { status: true, data } };
  });
};

export const getUserBalances = async (
  scope?: WalletScopeParams
): Promise<{
  data: ApiResponse<V2Balance[]>;
}> => {
  return getBalances(scope);
};

export const getWalletBalance = async (
  assetId: string,
  scope?: WalletScopeParams
): Promise<{ data: ApiResponse<V2Balance | null> }> => {
  const response = await getBalances(scope);
  const body = response?.data;
  const balances = extractResponseData<V2Balance[]>(body);

  if (!Array.isArray(balances)) {
    return { data: { status: false, data: null } };
  }

  const balance = balances.find(
    (b: V2Balance) => b.asset === assetId || b.symbol === assetId
  );

  return {
    data: {
      status: true,
      data: balance ?? null,
    },
  };
};

export const getAvailableBalances = async (
  balanceParams?: {
    type?: string;
  } & WalletScopeParams
): Promise<{ data: ApiResponse<V2PortfolioAsset[]> }> => {
  const result = await getPortfolioEstimateAssets(balanceParams);
  const body = result?.data;
  const portfolio = extractResponseData<V2Portfolio>(body);

  if (portfolio?.assets && balanceParams?.type) {
    const filtered = portfolio.assets.filter(
      (asset: V2PortfolioAsset) =>
        asset.currencyType === balanceParams.type ||
        asset.type === balanceParams.type
    );
    return {
      data: {
        status: true,
        data: filtered,
      },
    };
  }

  return {
    data: {
      status: body?.status ?? true,
      data: portfolio?.assets ?? [],
    },
  };
};

export const getWalletBalances = async (
  scope?: WalletScopeParams
): Promise<{
  data: ApiResponse<V2Portfolio>;
}> => {
  return getPortfolioEstimateAssets(scope);
};

export const getPortfolio = async (
  scope?: WalletScopeParams
): Promise<{
  data: ApiResponse<V2Portfolio>;
}> => {
  const data = await get<V2Portfolio>(
    walletPath("/api/v2/wallets/portfolio", scope),
    buildWalletScopeParams(scope)
  );
  return { data: { status: true, data } };
};

export const getPortfolioEstimateAssets = async (
  request?: PortfolioEstimateRequest
): Promise<{ data: ApiResponse<V2Portfolio> }> => {
  const currency = request?.currency?.trim() || "";
  const scopeKey = buildWalletScopeKey("portfolio_estimate", request);
  const key = currency ? `${scopeKey}:currency=${currency}` : scopeKey;
  return dedupWithTtl(key, PORTFOLIO_ESTIMATE_TTL_MS, async () => {
    const personalBodyScope = request?.businessId?.trim()
      ? {}
      : buildWalletScopeParams(request);
    const data = await create<V2Portfolio>(
      resolveBusinessUrlWithQuery(
        request?.businessId,
        "/api/v2/wallets/portfolio/estimate"
      ),
      {
        ...(request?.currency ? { currency: request.currency } : {}),
        ...personalBodyScope,
      }
    );
    return { data: { status: true, data } };
  }) as Promise<{ data: ApiResponse<V2Portfolio> }>;
};

export function unwrapV2PortfolioEstimateEnvelope(
  envelope: { data: ApiResponse<V2Portfolio> } | null | undefined
): V2Portfolio | null {
  if (!envelope?.data) {
    return null;
  }
  return extractResponseData<V2Portfolio>(envelope.data) ?? null;
}

export const getPortfolioPerformance = async (
  request: PortfolioPerformanceRequest
): Promise<{ data: ApiResponse<PortfolioPerformance> }> => {
  const start = request.startDate.includes("T")
    ? request.startDate
    : `${request.startDate}T00:00:00.000Z`;
  const end = request.endDate.includes("T")
    ? request.endDate
    : `${request.endDate}T23:59:59.999Z`;

  const params: Record<string, string> = {
    startDate: start,
    endDate: end,
    ...buildWalletScopeParams(request),
  };
  if (request.interval) params.interval = request.interval;

  const data = await get<PortfolioPerformance | Record<string, unknown>>(
    walletPath("/api/v2/wallets/portfolio/performance", request),
    params
  );
  return {
    data: {
      status: true,
      data: normalizePortfolioPerformance(data),
    },
  };
};

export const getPortfolioEstimate = async (
  startDate: string,
  endDate: string
): Promise<{ data: ApiResponse<PortfolioPerformance> }> => {
  return getPortfolioPerformance({ startDate, endDate });
};

export const refreshPortfolioAndBalances = async (
  scope?: WalletScopeParams
): Promise<void> => {
  const portfolioKey = buildWalletScopeKey("portfolio_estimate", scope);
  const balancesKey = buildWalletScopeKey("wallets_balances", scope);
  clearCacheEntriesByPrefix(portfolioKey);
  clearCacheEntriesByPrefix(balancesKey);

  try {
    await Promise.all([getPortfolioEstimateAssets(scope), getBalances(scope)]);
  } catch (err) {
    console.error("Error refreshing portfolio and balances:", err);
  }
};

export const getWalletAssets = getBalances;

export const getAssets = async (
  type?: string
): Promise<{ data: ApiResponse<LegacyAsset[]> }> => {
  const assets = await getTradingAssetsMetadata(type ?? null);

  if (!type) {
    return {
      data: {
        status: true,
        data: assets.data ?? [],
      },
    };
  }

  const assetData = assets.data ?? [];
  return {
    data: {
      status: true,
      data: assetData.filter(
        (asset: LegacyAsset) =>
          String(asset.type || "").toLowerCase() === type.toLowerCase()
      ),
    },
  };
};

export const getAssetWallet = async (data: AssetWalletRequest) => {
  if (!data.assetId) {
    throw new Error("assetId is required to fetch a wallet address");
  }

  const blockchainId = normalizeBlockchainIdentifier(
    data.blockchainId ?? data.chainId
  );

  if (!blockchainId) {
    throw new Error("blockchainId is required to fetch a wallet address");
  }

  const wallet = await create<CryptoReceiveAddressPayload>(
    resolveBusinessUrlWithQuery(
      data.businessId,
      "/api/v2/transactions/crypto/receive"
    ),
    {
      assetId: data.assetId,
      blockchainId,
      accountCategory: data.accountCategory,
    }
  );

  const normalizedAsset = wallet.asset?.toLowerCase() ?? "";
  const normalizedAddress = wallet.address ?? "";
  const normalizedTag = wallet.memo ?? wallet.tag ?? undefined;

  return {
    data: {
      status: true,
      data: {
        asset: normalizedAsset,
        walletId: normalizedAddress,
        walletIdLegacy: normalizedAddress,
        tag: normalizedTag,
        isTagRequired: wallet.isTagRequired ?? Boolean(normalizedTag),
      },
    },
  };
};

export const getNet = async (chainId: string) => ({
  data: {
    status: true,
    data: await get<Record<string, unknown>>(
      `/api/v2/blockchains/${encodeApiPathSegment(chainId, "chainId")}`
    ),
  },
});

export const createAddress = async (
  data: Record<string, unknown> & { businessId?: string | null }
) => {
  const { businessId, ...rest } = data;
  return create(
    resolveBusinessUrlWithQuery(businessId, "/api/v2/wallets/addresses"),
    rest
  );
};
